import { Link } from 'react-router-dom'
import { PublicNavBar } from '../components/PublicNavBar'
import { PublicFooter } from '../components/PublicFooter'
import { usePageMeta } from '../hooks/usePageMeta'
import { pageMeta } from '../seo/pageMeta'

const SECTIONS = [
  {
    title: 'Which providers power Factory Nexus',
    body: (
      <p>
        Agent tasks, code reviews, chat, and estimates are powered by third-party AI providers: Anthropic
        (Claude models), open models routed through OpenRouter, and DigitalOcean serverless inference. We name
        providers rather than specific model versions because the models in use are configured per deployment
        and improve over time.
      </p>
    ),
  },
  {
    title: 'Multi-provider routing with fallback',
    body: (
      <p>
        Requests are routed to the best available provider for the task and your plan. If a provider is
        unavailable or rate-limited, the platform automatically falls back to the next configured provider, so
        your task keeps running.
      </p>
    ),
  },
  {
    title: 'What data is sent',
    body: (
      <p>
        When you start a task, your prompt and the repository context relevant to that task — the files an agent
        needs to read or change — are sent to the provider handling the request. Data is only sent for tasks you
        initiate; connecting a repository does not by itself send anything to an AI provider.
      </p>
    ),
  },
  {
    title: 'Generated code belongs to you',
    body: (
      <p>
        Code produced by Factory Nexus agents for your projects is yours. We claim no ownership over your
        prompts, repositories, or generated output.
      </p>
    ),
  },
  {
    title: 'No training on your code',
    body: (
      <p>
        Factory Nexus does not use your code or prompts to train models. Your content is processed to complete
        the tasks you run — nothing more.
      </p>
    ),
  },
]

export default function HowWeUseAI() {
  usePageMeta(pageMeta('/how-we-use-ai'))

  return (
    <div className="min-h-screen text-gray-900 dark:text-gray-100">
      <PublicNavBar />

      <div className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="text-4xl font-bold mb-3 text-gray-900 dark:text-white">How we use AI</h1>
        <p className="text-gray-500 dark:text-gray-400 mb-12 text-lg">
          A plain-language description of the AI behind Factory Nexus — what powers it, what gets sent, and who
          owns the output.
        </p>

        <div className="space-y-8">
          {SECTIONS.map((section) => (
            <section key={section.title}>
              <h2 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">{section.title}</h2>
              <div className="text-gray-600 dark:text-gray-300 leading-relaxed text-[15px]">{section.body}</div>
            </section>
          ))}
        </div>

        <p className="mt-12 text-sm text-gray-500 dark:text-gray-400">
          For the full details, see our{' '}
          <Link to="/privacy" className="text-primary-600 dark:text-primary-400 hover:text-primary-500">Privacy Policy</Link>{' '}
          and{' '}
          <Link to="/terms" className="text-primary-600 dark:text-primary-400 hover:text-primary-500">Terms of Service</Link>,
          or email{' '}
          <a href="mailto:support@notify.factory-nexus.ai" className="text-primary-600 dark:text-primary-400 hover:text-primary-500">support@notify.factory-nexus.ai</a>.
        </p>
      </div>

      <PublicFooter />
    </div>
  )
}
